import { BeratungsProtokoll, GvSpartenInfo, GvTarif, LvSpartenInfo, UvSpartenInfo } from '@mng/salesmodel';
import { Partner, PartnerRaVseWithExpiry, VermittlerDto } from '@mng/domain';

export interface SalesContext {
  angebote?: Angebot[];
  antraege?: Antrag[];
  blickTarifInputs?: GvBlickTarifInputParams[];
  beschreibung?: string;
  erstellerAbberNr?: number;
  erstelltVon?: number;
  erstellungsdatum?: string;
  id?: string;
  partnerAngebotBeziehungen?: PartnerRefAngebotBeziehung[];
  partners?: Partner[];
  version?: number;
  zuletztGeaendert?: string;
  documentInfo?: string;
}

export interface Angebot {
  beschreibung?: string;
  angebotsDaten?: string;
  angestelltenRabatt?: boolean;
  antragNr?: number;
  antragTeilNr?: number;
  contextId?: string;
  erstelltVon?: number;
  erstellungsdatum?: string;
  geschaeftsfall?: Geschaeftsfall;
  gueltigBis?: string;
  id?: string;
  praemie?: number;
  sparte?: AngebotSparte;
  tarifInformation?: TarifInformation;
  version?: number;
  zahlweise?: string;
  zuletztGeaendert?: string;
  status?: AngebotStatus;
  fehlendeAngebotTeile?: string[];
  statusInfo?: SalesContextStatusInfo;
  unterschriftsmappenOwner?: string;
  vermittlerInfoBlattAbberNr?: number;
}

export interface Geschaeftsfall {
  id?: number;
  name?: string;
  sparte?: string;
  ueberleitbar?: boolean;
  neugeschaeft?: boolean;
}

export enum AngebotSparte {
  G = 'G',
  L = 'L',
  U = 'U',
  S = 'S'
}

export interface TarifInformation {
  abschlusswerber?: VermittlerDto;
  bezeichnung?: string;
  langBezeichnung?: string;
  schluessel?: string;
  spartenInfo?: LvSpartenInfo | GvSpartenInfo | UvSpartenInfo;
  vpmsBerechtigungBerechner?: number;
}

export enum AngebotStatus {
  INVALID = 'INVALID',
  VALID = 'VALID',
  BEANTRAGBAR = 'BEANTRAGBAR',
  UNVERAENDERT = 'UNVERAENDERT'
}

/**
 * A structured object holding the calculated status text and its severity.
 * This corresponds to the SalesContextStatusInfo record on the Java backend.
 */
export interface SalesContextStatusInfo {
  text: string;
  type: SalesContextStatusInfoSeverity;
}

/**
 * Represents the severity of the SalesContextStatusInfo.
 * This corresponds to the Severity enum on the Java backend.
 */
export type SalesContextStatusInfoSeverity = 'info' | 'warning' | 'error';

export interface Antrag {
  beschreibung?: string;
  erstelltVon?: number;
  erstelltVonAbber?: number;
  erstellungsdatum?: string;
  eingereichtAm?: string;
  id?: string;
  status?: AntragStatus;
  unterschriebenAm?: string;
  version?: number;
  zuletztGeaendert?: string;
  salesContextId?: string;
  angebotReferenzen?: string[];
  schemaReferenz?: string;
  antragsdaten?: string;
  signaturStatus?: SignierungsStatus;
  esignSessionId?: string;
  ueberleitungsArt?: UeberleitungsArt;
  beratungsProtokoll?: BeratungsProtokoll;
  statusInfo?: SalesContextStatusInfo;
  omds3?: boolean;
}

export enum AntragStatus {
  NICHT_UNTERSCHREIBBAR = 'NICHT_UNTERSCHREIBBAR',
  UNTERSCHREIBBAR = 'UNTERSCHREIBBAR',
  IN_UNTERSCHRIFT = 'IN_UNTERSCHRIFT',
  UNTERSCHRIEBEN = 'UNTERSCHRIEBEN',
  UEBERGELEITET = 'UEBERGELEITET'
}

export type SignierungsStatus =
  | 'NOT_INITIATED'
  | 'PDF_CREATION'
  | 'ERROR_DURING_PDF_CREATION'
  | 'WAIT_FOR_SIGNATURES'
  | 'ERROR_SIGNING'
  | 'SIGNED'
  | 'ERROR_AUTOPOL'
  | 'AUTOPOL_EINGELANGT'
  | 'AUTOPOL_ERSTVERARBEITET'
  | 'ERROR_DMS_STORAGE'
  | 'STORED_RICH'
  | 'STORED_FLAT'
  | 'ERROR_UEBERLEITUNG'
  | 'UEBERGELEITET'
  | 'COMPLETE';

/**
 * This corresponds to the UeberleitungsArt enum on the Java backend.
 */
export type UeberleitungsArt = 'DIREKT' | 'ESIGN' | 'OMDS3';

export interface GvBlickTarifInputParams {
  alterBis?: string;
  sparte?: AngebotSparte;
  alterVon?: string;
  alterMode?: string;
  gvAlterDaten?: GvAlterDaten;
  beschreibung?: string;
  contextId?: string;
  documentInfo?: string;
  erstelltVon?: number;
  familienBonus?: boolean;
  fzKonto?: string;
  id?: string;
  region?: string;
  sozialVersicherung?: string;
  tarife?: GvTarif[];
  version?: number;
  tarifVersionStichtag?: string;
  erstellungsdatum?: string;
  zuletztGeaendert?: string;
  columnsPerPage?: number;
  printFzKonto?: boolean;
  printFzRabatt?: boolean;
  tarifBezMode?: TarifBezeichnungMode;
}

export interface GvAlterDaten {
  versBeginn: string;
  gvAlters: GvAlter[];
}

export interface GvAlter {
  gebDatum: string;
  alter: number;
}

export type TarifBezeichnungMode = 'lang' | 'mittel' | 'kurz';

export interface PartnerRefAngebotBeziehung {
  angebotId?: string;
  partnerId?: string;
  rollen?: string[];
}

export interface AntragDto {
  antrag?: Antrag;
  salesContext?: SalesContext;
  vsen?: { [partnerId: string]: PartnerRaVseWithExpiry }; // TODO: enable typed map support in client tools
}

export enum PartnerRefAngebotBeziehungRollen {
  VERSICHERTE_PERSON_1 = 'VERSICHERTE_PERSON1',
  VERSICHERTE_PERSON_2 = 'VERSICHERTE_PERSON2',
  VERSICHERUNGSNEHMER = 'VERSICHERUNGSNEHMER',
  VERSICHERUNGSNEHMER_2 = 'VERSICHERUNGSNEHMER2',
  RENTENEMPFAENGER = 'RENTENEMPFAENGER',
  RENTENPARTNER = 'RENTENPARTNER',
  BESITZER = 'BESITZER',
  MITBESITZER = 'MITBESITZER',
  PRAEMIENZAHLER = 'PRAEMIENZAHLER',
  GESETZLICHER_VERTRETER = 'GESETZLICHER_VERTRETER',
  TREUHAENDER = 'TREUHAENDER',
  TREUGEBER = 'TREUGEBER',
  WIRTSCHAFTLICHER_EIGENTUEMER = 'WIRTSCHAFTLICHER_EIGENTUEMER',
  ZEICHNUNGSBERECHTIGTER = 'ZEICHNUNGSBERECHTIGTER'
}

export type Sparte = 'GV' | 'LV' | 'UV' | 'SV';

export const angebotSpartenLabel: { [sparteKey: string]: string } = {
  [AngebotSparte.G]: 'GV',
  [AngebotSparte.L]: 'LV',
  [AngebotSparte.U]: 'UV',
  [AngebotSparte.S]: 'SV'
};

export enum AngebotZahlweise {
  EINMALERLAG = 'EINMALERLAG',
  JAEHRLICH = 'JAEHRLICH',
  HALBJAEHRLICH = 'HALBJAEHRLICH',
  VIERTELJAEHRLICH = 'VIERTELJAEHRLICH',
  MONATLICH = 'MONATLICH'
}

export interface SalesContextSearchOverview {
  angebote?: { sparte: AngebotSparte }[];
  antraege?: AntragStateInfo[];
  beschreibung?: string;
  erstellerAbberNr?: number;
  erstelltVon?: number;
  erstellungsdatum?: string;
  id?: string;
  version?: number;
  zuletztGeaendert?: string;
  documentInfo?: string;
}

export interface SalesContextClearanceOverview {
  angebote?: { sparte: AngebotSparte }[];
  antraege?: AntragStateInfo[];
  beschreibung?: string;
  erstellerAbberNr?: number;
  erstellerInfo?: Vermittler;
  erstelltVon?: number;
  erstellungsdatum?: string;
  id?: string;
  version?: number;
  zuletztGeaendert?: string;
  documentInfo?: string;
  blickTarifInputs: GvBlickTarifInputParams[];
}

export interface AntragStateInfo {
  id?: string;
  status?: AntragStatus;
}

export interface Vermittler {
  vermittlerName?: VermittlerName;
  vermittlerNr?: number;
}

export interface VermittlerName {
  nachname?: string;
  vorname?: string;
}

export interface AngebotContainer {
  angebot?: Angebot;
  partnerBeziehungen?: PartnerAngebotBeziehung[];
}

export interface PartnerAngebotBeziehung {
  angebotId?: string;
  partner?: Partner;
  rollen?: string[];
}
